import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DocDetail, DocVideoPayload } from "./types.js";

import { Markdown } from "./markdown.js";

/**
 * One-video-per-document viewer.
 *
 * A video doc is shown as an actual **video**, never as a PDF or a markdown
 * wall:
 *   • `kind === "upload"` (a hosted MP4 attachment) → native `<video controls>`.
 *   • a resolved `direct_url` (a raw video-FILE link — Dropbox share links
 *     normalize to dl.dropboxusercontent.com server-side, any pasted `….mp4`)
 *     → the same native `<video controls>`. NEVER framed: the original share
 *     page refuses framing, which rendered the empty black stage (the
 *     2026-07-16 "black square" bug).
 *   • hosted (`youtube` / `vimeo` / `embed`, incl. Google Drive `/preview`)
 *     with a resolved `provider_url` → an `<iframe>` player.
 *   • none of those (e.g. an imported training video that only links its
 *     source page) → a poster + "Watch the video ↗" button to `external_url`,
 *     so the reader still points at something playable instead of dumping
 *     scraped text.
 *
 * The URL → payload normalization lives server-side in the portal-api
 * `Documents::VideoMetadata` (YouTube watch/shorts/live → `/embed/`, Drive
 * `/view` → `/preview`, Dropbox share → direct bytes).
 *
 * The optional markdown body is shown below as the description. Chapters (when
 * present) render as a deep-link rail for hosted players (`?start=` is honoured
 * by YouTube + Vimeo) and as seek buttons for uploads.
 *
 * Hosted embeds need the player origin in the desktop shell's CSP `frame-src`
 * (`apps/desktop/src-tauri/tauri.conf.json`) — YouTube + Vimeo + Google Drive
 * are allow-listed there — and direct links ride its `media-src` (https:); the
 * change reaches installed desktop apps only on a rebuild. The web app has no
 * CSP. Pinned by `VideoReader.test.tsx` + `desktopCsp.test.ts` + the video
 * case in `documentation.cy.ts`.
 */
export function VideoReader({ detail }: { detail: DocDetail }): JSX.Element {
  const video = detail.video ?? null;
  const description = detail.content?.trim() ? detail.content : null;

  return (
    <Box data-testid="video-reader" sx={{ display: "grid", gap: 2 }}>
      <VideoStage detail={detail} video={video} />

      {video && (video.chapters?.length ?? 0) > 0 && (
        <Chapters video={video} />
      )}

      {description && (
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 1 }}>
            About this video
          </Typography>
          <Box sx={{ maxWidth: "76ch" }}>
            <Markdown source={description} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

const STAGE_SX = {
  position: "relative" as const,
  width: "100%",
  aspectRatio: "16 / 9",
  borderRadius: 1,
  overflow: "hidden",
  bgcolor: "#000",
  border: 1,
  borderColor: "divider",
};

function VideoStage({
  detail,
  video,
}: {
  detail: DocDetail;
  video: DocVideoPayload | null;
}): JSX.Element {
  const uploadSrc = video?.file?.preview_url ?? video?.file?.download_url ?? null;

  // Uploaded MP4 → native player.
  if (video?.kind === "upload" && uploadSrc) {
    return (
      <Box sx={STAGE_SX}>
        <Box
          component="video"
          controls
          preload="metadata"
          poster={video.thumbnail ?? undefined}
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
        >
          <source src={uploadSrc} type={video.file?.content_type ?? undefined} />
          Your browser does not support inline video playback.{" "}
          <a href={uploadSrc} target="_blank" rel="noopener noreferrer">
            Open the file directly.
          </a>
        </Box>
      </Box>
    );
  }

  // Direct video-file link (Dropbox → dl.dropboxusercontent.com, raw .mp4)
  // → native player, same as an upload. Framing the original share page
  // instead is what produced the empty black square.
  if (video?.direct_url) {
    return (
      <Box sx={STAGE_SX}>
        <Box
          component="video"
          controls
          preload="metadata"
          poster={video.thumbnail ?? undefined}
          src={video.direct_url}
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
        >
          Your browser does not support inline video playback.{" "}
          <a href={video.direct_url} target="_blank" rel="noopener noreferrer">
            Open the file directly.
          </a>
        </Box>
      </Box>
    );
  }

  // Hosted embed (YouTube / Vimeo / Google Drive preview / arbitrary embed URL).
  if (video?.provider_url) {
    return (
      <Box sx={STAGE_SX}>
        <Box
          component="iframe"
          title={detail.title}
          src={appendStartTime(video.provider_url, video.chapters?.[0]?.time ?? 0)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </Box>
    );
  }

  // No playable source — point at the original page if we have one.
  const fallbackUrl = detail.external_url ?? null;
  return (
    <Box
      sx={{
        ...STAGE_SX,
        display: "grid",
        placeItems: "center",
        ...(video?.thumbnail
          ? {
              backgroundImage: `url(${video.thumbnail})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}),
      }}
    >
      <Box sx={{ display: "grid", gap: 1.5, placeItems: "center", p: 3, bgcolor: alpha("#000", 0.55), borderRadius: 1 }}>
        <Typography sx={{ fontSize: 13, color: "common.white", textAlign: "center" }}>
          {fallbackUrl ? "This video plays on its source page." : "This video has no playable source yet."}
        </Typography>
        {fallbackUrl && (
          <Button
            component="a"
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="small"
          >
            Watch the video ↗
          </Button>
        )}
      </Box>
    </Box>
  );
}

function Chapters({ video }: { video: DocVideoPayload }): JSX.Element {
  const chapters = video.chapters ?? [];
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Typography sx={{ px: 1.5, py: 1, fontSize: 12, fontWeight: 600, color: "text.secondary", borderBottom: 1, borderColor: "divider" }}>
        Chapters · {chapters.length}
      </Typography>
      <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", maxHeight: "40vh", overflowY: "auto" }}>
        {chapters.map((c, i) => {
          const href = video.provider_url ? appendStartTime(video.provider_url, c.time) : null;
          const label = (
            <>
              <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: "text.disabled", width: 52, flexShrink: 0 }}>
                {formatDuration(c.time)}
              </Box>
              <Box component="span" sx={{ fontSize: 14 }}>{c.title}</Box>
            </>
          );
          return (
            <Box component="li" key={`${c.time}-${i}`} sx={{ borderTop: i > 0 ? 1 : 0, borderColor: "divider" }}>
              {href ? (
                <Box
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "flex", alignItems: "baseline", gap: 1.5, px: 1.5, py: 1, textDecoration: "none", color: "text.primary", "&:hover": { bgcolor: "action.hover" } }}
                >
                  {label}
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, px: 1.5, py: 1 }}>{label}</Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** `mm:ss` / `h:mm:ss`. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** YouTube + Vimeo both honour `?start=NN` (seconds) on the embed URL. */
function appendStartTime(url: string, seconds: number): string {
  if (!url || !Number.isFinite(seconds) || seconds <= 0) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}start=${Math.floor(seconds)}`;
}
